export interface LoginResponseBody {
    success: boolean;
    token: string;
    type: UserType;
}

export interface MainResponseBody {
    _id: string;
    email: string;
}

export type DataName = 'event' | 'status';

export type LayerName = 'UserInfo' | 'PSDSettings' | 'CameraSettings';

export interface UserConfigUserInfo {
    ID: string;
    Email: string;
}

export type RangeType = 'AUTO' | 'MANUAL';

export interface UserConfigPSDSettings {
    L1Visibility: boolean;
    L2Visibility: boolean;
    RangeType: RangeType;
    PowerMin: number;
    PowerMax: number;
}

export interface SingleCameraSettings {
    Name: string;
    CameraPos: number[];
    TargetPos: number[];
}

export interface UserConfigCameraSettings {
    CurrentCamera: string;
    Cameras: SingleCameraSettings[];
}

export enum ActionType {
    LOAD_ME_CONFIG = 'LOAD_ME_CONFIG',
    CHANGE_LAYER_FIELD = 'CHANGE_LAYER_FIELD',
    UDPATE_LAYER_DATA = 'UDPATE_LAYER_DATA',
    REQUEST_USER_CONFIG_STATE = 'REQUEST_USER_CONFIG_STATE',
    REQUEST_USER_LIST = 'REQUEST_USER_LIST',
    REQUEST_CREATE_USER = 'REQUEST_CREATE_USER',
    REQUEST_UPDATE_USER = 'REQUEST_UPDATE_USER',
    REQUEST_DELETE_USER = 'REQUEST_DELETE_USER',
    REQUEST_UPDATE_USER_PASSWORD = 'REQUEST_UPDATE_USER_PASSWORD',
    CLEAR_USER_EDIT_CACHE = 'CLEAR_USER_EDIT_CACHE',
    ACTIVITY_STATUS_REQUEST= 'ACTIVITY_STATUS_REQUEST',
    ACTIVITY_STATUS_SUCCESS= 'ACTIVITY_STATUS_SUCCESS',
    ACTIVITY_STATUS_FAILURE= 'ACTIVITY_STATUS_FAILURE',
    ACTIVITY_STATUS_REQUEST_ALL = 'ACTIVITY_STATUS_REQUEST_ALL',
    ACTIVITY_STATUS_SUCCESS_ALL = 'ACTIVITY_STATUS_SUCCESS_ALL',
    ACTIVITY_STATUS_FAILURE_ALL = 'ACTIVITY_STATUS_FAILURE_ALL',
    ACTIVITY_SPENT_TIME_REQUEST= 'ACTIVITY_SPENT_TIME_REQUEST',
    ACTIVITY_SPENT_TIME_SUCCESS= 'ACTIVITY_SPENT_TIME_SUCCESS',
    ACTIVITY_SPENT_TIME_FAILURE= 'ACTIVITY_SPENT_TIME_FAILURE',
}

export interface ActionChangeLayerField {
    type: ActionType.CHANGE_LAYER_FIELD;
    payload: {
        layerName: LayerName;
        fieldKey: string;
        fieldValue: any;
    };
}

export interface ActionUpdateLayerData {
    type: ActionType.UDPATE_LAYER_DATA;
    payload: {
        dataName: DataName;
        data: GristEvent | GristStatus;
    };
}

export interface BaseAction {
    type: ActionType;
    payload: any;
}

export interface ActionLoadMainConfig {
    type: ActionType.LOAD_ME_CONFIG;
    payload: MainConfigResponseBody;
}

export interface ActionRequestState {
    type: ActionType;
    payload: HttpState;
}

export interface ActionRequestStateWithData<T> {
    type: ActionType;
    state: HttpState;
    payload: T;
}

export interface MainReportResponseBody {
    Report: string;
}

export interface MainConfigResponseBody {
    UserInfo: UserConfigUserInfo;
    PSDSettings: UserConfigPSDSettings;
    CameraSettings: UserConfigCameraSettings;
}

export interface SNEventInfo {
    SN: string;
    StartTime: string[];
}

export interface SNEvent {
    L1Jammer: SNEventInfo[];
    L2Jammer: SNEventInfo[];
    L1Spoofer: SNEventInfo[];
    L2Spoofer: SNEventInfo[];
}

export interface SNInfo {
    SN: string;
    Pos: number[];
    ReportFile: string;
}

export interface AOAEpochInfo {
    Azi: number;
    Ele: number;
    Pos: number[];
    Valid: boolean;
}

export interface AOAInfo {
    Time: string;
    AOA: AOAEpochInfo[];
}

export interface TDOAEpochInfo {
    Delay: number;
    Roll: number;
    Pos: number[][];
    Valid: boolean;
}

export interface TDOAInfo {
    Time: string;
    TDOA: TDOAEpochInfo[];
}

export interface SolInfo {
    Time: string;
    Pos: number[][];
}

export interface OrbitInfo {
    Pos: number[];
    TLE: string[];
    OrbitPlane: number[];
    GroundTrack: number[];
}

export interface ErrorInfo {
    Time: string;
    IntegratedPrecision: number[];
}

export interface CommonEventInfo {
    StartTime: string;
    StopTime: string;
    SN: SNInfo[];
    AOA: AOAInfo[];
    TDOA: TDOAInfo[];
    Sol: SolInfo[];
    Orbit: OrbitInfo[];
    Error: ErrorInfo[];
}

export interface CommonEvent {
    L1Jammer: CommonEventInfo[];
    L2Jammer: CommonEventInfo[];
    L1Spoofer: CommonEventInfo[];
    L2Spoofer: CommonEventInfo[];
}

export interface GristEvent {
    Time: string;
    SNEvent: SNEvent;
    CommonEvent: CommonEvent;
}

export interface PSDArrayInfo {
    Band: string;
    Freq: number[];
    Power: number[];
}

export interface PSDInfo {
    Time: string;
    Array: PSDArrayInfo[];
}

export interface SatXYInfo {
    x: number[];
    y: number[];
    text: string[];
}

export interface AOAXYInfo {
    x: number[];
    y: number[];
}

export interface BFArrayInfo {
    Band: string;
    Beamformer: number[][];
    Sat: SatXYInfo;
    Jammer: AOAXYInfo;
    Spoofer: AOAXYInfo;
}

export interface BFInfo {
    Time: string;
    Array: BFArrayInfo[];
}

export interface GristStatusElement {
    Time: string;
    Name: string;
    PSD: PSDInfo;
    BF: BFInfo;
}

export interface GristStatus {
    Time: string;
    GristStatus: GristStatusElement[];
}

export interface GristData {
    GristEvent: GristEvent;
    GristStatus: GristStatus;
}

export enum HttpState {
    Idle = 'Idle',
    Progress = 'Progress',
    Success = 'Success',
    Failure = 'Failure'
}

export interface UserData {
    _id: string;
    email: string;
    usertype: UserType;
    enabled: boolean;
    alertL1: boolean;
    alertL2: boolean;
    minPeriod: number;
    maxPower: number;
}

export interface EmailTakenResponseBody {
    emailtaken: boolean;
}

export interface CreateUserRequestBody {
    email: string;
    password: string;
    usertype: string;
    enabled: boolean;
}

export interface UpdateUserRequestBody {
    usertype: string;
    enabled: boolean;
}

export type UserType = 'admin' | 'user' | 'moderator';

export interface MainInfo {
    id: string;
    email: string;
    usertype: UserType;
    iat: number;
}

export type ConfigActions = ActionLoadMainConfig | ActionRequestState | ActionChangeLayerField;
